import * as Y from 'yjs';
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';
import * as syncProtocol from 'y-protocols/sync';
import * as awarenessProtocol from 'y-protocols/awareness';
import { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Custom Yjs provider that uses Supabase Realtime as the transport layer.
 * This eliminates the need for a separate WebSocket server while providing
 * efficient CRDT-based collaboration.
 */
export class SupabaseProvider {
  private doc: Y.Doc;
  private channel: RealtimeChannel;
  private awareness: awarenessProtocol.Awareness;
  private workflowId: string;
  private clientId: string;
  private synced = false;
  private isApplyingRemote = false; // Flag to prevent broadcast loops

  constructor(doc: Y.Doc, channel: RealtimeChannel, workflowId: string) {
    console.log('[Yjs Provider] Constructor called for workflow:', workflowId);

    this.doc = doc;
    this.channel = channel;
    this.workflowId = workflowId;
    this.clientId = Math.random().toString(36).substring(7); // Generate unique client ID
    this.awareness = new awarenessProtocol.Awareness(doc);

    console.log('[Yjs Provider] Client ID:', this.clientId);
    console.log('[Yjs Provider] Y.Doc ID:', (doc as any).__debugId);

    // Bind Yjs update handler
    this.doc.on('update', this.handleDocUpdate);
    console.log('[Yjs Provider] Listening to doc updates');

    // Add test listener to verify events are firing
    this.doc.on('update', (update: Uint8Array, origin: any) => {
      console.log(
        '[Yjs Provider] 🔔 TEST LISTENER - update event fired! Origin:',
        origin,
        'is provider?',
        origin === this,
      );
    });

    // Bind awareness change handler
    this.awareness.on('change', this.handleAwarenessChange);

    // Listen for remote updates via Supabase
    this.channel.on(
      'broadcast',
      { event: 'yjs:update' },
      this.handleRemoteUpdate,
    );
    console.log('[Yjs Provider] Listening to yjs:update events');

    // Listen for sync requests from newly joined clients
    this.channel.on(
      'broadcast',
      { event: 'yjs:sync-request' },
      this.handleSyncRequest,
    );
    // Listen for sync responses targeted to us
    this.channel.on(
      'broadcast',
      { event: 'yjs:sync-response' },
      this.handleSyncResponse,
    );

    // Listen for awareness updates
    this.channel.on(
      'broadcast',
      { event: 'yjs:awareness' },
      this.handleRemoteAwareness,
    );
    console.log('[Yjs Provider] Listening to yjs:awareness events');

    // Request initial sync
    this.requestSync();
    console.log('[Yjs Provider] Initial sync requested');
  }

  /**
   * Handle local document updates and broadcast to other clients
   */
  private handleDocUpdate = (update: Uint8Array, origin: any) => {
    // Don't broadcast updates that came from remote
    if (this.isApplyingRemote) {
      console.log('[Yjs Provider] Skipping broadcast - applying remote update');
      return;
    }

    console.log(
      '[Yjs Provider] Broadcasting local update, origin:',
      origin,
      'update size:',
      update.length,
    );

    // Broadcast raw update directly (Yjs updates are already encoded)
    this.channel.send({
      type: 'broadcast',
      event: 'yjs:update',
      payload: {
        update: Array.from(update), // Send raw Yjs update
        workflowId: this.workflowId,
        clientId: this.clientId,
      },
    });
  };

  /**
   * Handle awareness changes (user presence, cursors, selections)
   */
  private handleAwarenessChange = ({
    added,
    updated,
    removed,
  }: {
    added: number[];
    updated: number[];
    removed: number[];
  }) => {
    const changedClients = added.concat(updated).concat(removed);
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, 1); // Message type: awareness
    encoding.writeVarUint8Array(
      encoder,
      awarenessProtocol.encodeAwarenessUpdate(this.awareness, changedClients),
    );

    this.channel.send({
      type: 'broadcast',
      event: 'yjs:awareness',
      payload: {
        update: Array.from(encoding.toUint8Array(encoder)),
        workflowId: this.workflowId,
        clientId: this.clientId,
      },
    });
  };

  /**
   * Handle remote Yjs updates from other clients
   */
  private handleRemoteUpdate = ({ payload }: any) => {
    if (payload.workflowId !== this.workflowId) return;

    // Filter out our own messages
    if (payload.clientId === this.clientId) {
      console.log('[Yjs Provider] Ignoring own broadcast');
      return;
    }

    console.log(
      '[Yjs Provider] Received remote update from client:',
      payload.clientId,
      'size:',
      payload.update.length,
    );

    // Apply raw Yjs update directly
    const update = new Uint8Array(payload.update);
    console.log(
      '[Yjs Provider] Applying update to Y.Doc:',
      (this.doc as any).__debugId,
    );

    // Set flag to prevent broadcast loop
    this.isApplyingRemote = true;

    try {
      // Check doc state before (arrays)
      const nodesBefore = this.doc.getArray('nodes').length;
      const edgesBefore = this.doc.getArray('edges').length;

      // Apply update with explicit origin so downstream listeners can distinguish it
      Y.applyUpdate(this.doc, update, this);

      // Check doc state after (arrays)
      const nodesAfter = this.doc.getArray('nodes').length;
      const edgesAfter = this.doc.getArray('edges').length;

      console.log(
        '[Yjs Provider] ✅ Update applied to Y.Doc:',
        (this.doc as any).__debugId,
        {
          before: { nodes: nodesBefore, edges: edgesBefore },
          after: { nodes: nodesAfter, edges: edgesAfter },
          changed: nodesBefore !== nodesAfter || edgesBefore !== edgesAfter,
        },
      );
    } finally {
      // Always reset flag
      this.isApplyingRemote = false;
    }
  };

  /**
   * Respond to sync requests by sending the current document state
   */
  private handleSyncRequest = ({ payload }: any) => {
    if (payload.workflowId !== this.workflowId) return;
    // Ignore our own request
    if (payload.clientId === this.clientId) return;

    // Encode the entire current document state as a Yjs update
    const fullUpdate = Y.encodeStateAsUpdate(this.doc);
    this.channel.send({
      type: 'broadcast',
      event: 'yjs:sync-response',
      payload: {
        update: Array.from(fullUpdate),
        workflowId: this.workflowId,
        clientId: this.clientId,
        targetClientId: payload.clientId,
      },
    });
  };

  /**
   * Apply sync responses that are specifically targeted to this client
   */
  private handleSyncResponse = ({ payload }: any) => {
    if (payload.workflowId !== this.workflowId) return;
    // Only apply if the response is intended for us
    if (payload.targetClientId !== this.clientId) return;

    const update = new Uint8Array(payload.update);
    // Prevent rebroadcast while applying sync state
    this.isApplyingRemote = true;
    try {
      Y.applyUpdate(this.doc, update, this);
      this.synced = true;
      console.log(
        '[Yjs Provider] ✅ Initial sync applied for Y.Doc:',
        (this.doc as any).__debugId,
      );
    } finally {
      this.isApplyingRemote = false;
    }
  };

  /**
   * Handle remote awareness updates (other users' presence)
   */
  private handleRemoteAwareness = ({ payload }: any) => {
    if (payload.workflowId !== this.workflowId) return;

    // Filter out our own messages
    if (payload.clientId === this.clientId) return;

    const update = new Uint8Array(payload.update);
    const decoder = decoding.createDecoder(update);
    const messageType = decoding.readVarUint(decoder);

    if (messageType === 1) {
      const awarenessUpdate = decoding.readVarUint8Array(decoder);
      awarenessProtocol.applyAwarenessUpdate(
        this.awareness,
        awarenessUpdate,
        this,
      );
    }
  };

  /**
   * Request initial sync from other connected clients
   */
  private requestSync = () => {
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, 0); // Message type: sync step 1
    syncProtocol.writeSyncStep1(encoder, this.doc);

    this.channel.send({
      type: 'broadcast',
      event: 'yjs:sync-request',
      payload: {
        update: Array.from(encoding.toUint8Array(encoder)),
        workflowId: this.workflowId,
        clientId: this.clientId,
      },
    });
  };

  /**
   * Public method to request a sync from peers
   */
  public requestSyncFromPeers() {
    this.requestSync();
  }

  /**
   * Set local awareness state (user info, cursor position, etc.)
   */
  public setAwarenessState(state: Record<string, any>) {
    this.awareness.setLocalState(state);
  }

  /**
   * Get awareness states of all connected clients
   */
  public getAwarenessStates() {
    return this.awareness.getStates();
  }

  /**
   * Cleanup and destroy the provider
   */
  public destroy() {
    this.doc.off('update', this.handleDocUpdate);
    this.awareness.off('change', this.handleAwarenessChange);
    this.awareness.destroy();
  }

  /**
   * Get the awareness instance for external use
   */
  public getAwareness() {
    return this.awareness;
  }

  /**
   * Check if initial sync is complete
   */
  public isSynced() {
    return this.synced;
  }

  /**
   * Get the Y.Doc instance (for debugging)
   */
  public getDoc() {
    return this.doc;
  }
}
